import { MessageRatingType } from '@types';
import axios, { AxiosInstance } from 'axios';
class ApiClient {

    client: AxiosInstance;

    isMemberMode: boolean = false;

    widgetToken: string | null = null;
    sessionId: string | null = null;
    visitorToken: string | null = null;
    
    nonce: string | null = null;

    accessToken: string | null = null;
    refreshToken: string | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: process.env.REACT_APP_WEB_WIDGET_URL || '',
        });
        this.client.interceptors.request.use((config) => {
            const defaultConfig = this.config;
            return {
                ...config,
                headers: {
                    ...defaultConfig.headers,
                    ...(config.headers ?? {}),
                },
                params: {
                    ...defaultConfig.params,
                    ...(config.params ?? {}),
                },
            };
        });
    }

    setIsMemberMode(flag: boolean) {
        this.isMemberMode = flag;
    }

    setWidgetToken(token: string) {
        this.widgetToken = token;
    }

    setSessionId(id: string) {
        this.sessionId = id;
    }

    setVisitorToken(token: string) {
        this.visitorToken = token;
    }

    setNonce(nonce: string) {
        this.nonce = nonce;
    }
    
    setAccessToken(token: string) {
        this.accessToken = token;
    }

    setRefreshToken(token: string) {
        this.refreshToken = token;
    }

    get config() {
        return {
            headers: {
                "x-imbee-web-chat-token": this.widgetToken,
            },
            params: {
                "session_id": this.sessionId,
                "visitor_token": this.visitorToken,
                // "access_token": this.accessToken,
            }
        };
    }

    async fetchWidget() {
        return this.client.get('/widget')
    }

    /**
     * IMBEE-8955 (T115) — a visitor-side event for the PRD Tracking plan.
     *
     * Fire-and-forget by design, and it swallows its own failures: this runs on a stranger's
     * website alongside their code, and a widget that threw or logged noise because an analytics
     * endpoint was unreachable would be a bug in their page, not ours. channel_id is added by the
     * service from the token, so it is deliberately not sent here.
     *
     * No per-call config: the request interceptor in the constructor already merges
     * `this.config.headers` into every outgoing request, and the other twelve call sites in this
     * class all rely on it. Passing them here spread the same object on top of itself — a no-op
     * that read like headers needed setting by hand.
     */
    trackEvent(event: string, properties?: Record<string, unknown>) {
        this.client.post('/widget/track', { event, properties }).catch(() => undefined);
    }

    async fetchVisitorNonce() {
        const response = await this.client.post('/auth/visitor', {
            visitor_token: this.visitorToken,
        });
        if(response.data?.nonce) {
            this.setNonce(response.data.nonce);
        }
        if(!this.isMemberMode && !response.data?.exist_session) {
            this.setSessionId('');
        }
    }

    async fetchAccessToken() {
        const payload = this.isMemberMode
            ? {
                nonce: this.nonce,
                unique_id: this.visitorToken,
            }
            : {
                nonce: this.nonce,
                visitor_token: this.visitorToken,
            };

        const response = await this.client.post('/auth/token', payload);
        const data = response.data;
        this.setSessionId(data.session_id || '');
        this.setAccessToken(data.access_token || '');
        this.setRefreshToken(data.refresh_token || '');
    }

    async refreshAccessToken() {
        const response = await this.client.post('/auth/refresh', {
            access_token: this.accessToken,
            refresh_token: this.refreshToken,
        });
        this.setAccessToken(response.data.access_token || '');
        this.setRefreshToken(response.data.refresh_token || '');
    }

    async loadSession() {
        const response = await this.client.post(`/sessions/${this.sessionId}/restore`, {
            visitor_token: this.visitorToken,
        }).catch(err => {
            this.setSessionId('')
            throw err;
        });
        return response;
    }

    /**
     * `campaignShowId` — DC-T114-108. Present only on the message the visitor sends while a campaign
     * greeting is the reason the widget is open, so the agent's inbox can show WHICH greeting
     * produced this message. A property of the MESSAGE, not the conversation: one thread can carry
     * several campaign engagements over time, mirroring T22's per-message `post` (imbee-cms !3998).
     *
     * Omitted entirely when there is no engaged show — the service runs `whitelist: true`, so a
     * `null` would be stripped anyway and an absent key states "ordinary message" unambiguously.
     */
    async sendTextMessage(text: string, senderIdentifier: string, campaignShowId?: string | null) {
        return this.client.post('/message', {
            text: text,
            sender_identifier: senderIdentifier,
            visitor_token: this.visitorToken,
            ...(campaignShowId ? { campaign_show_id: campaignShowId } : {}),
        }).catch(err => {
            console.log(err);
            throw err;
        });
    }

    async sendReplyMessage(title: string, id: string, targetType: string, targetId: string, senderIdentifier: string) {
        return this.client.post('/message/reply', {
            reply: {
                target_type: targetType,
                target_id: targetId,
                button_reply: {
                    id: id,
                    title: title
                }
            },
            visitor_token: this.visitorToken,
            sender_identifier: senderIdentifier
        }).catch(err => {
            console.log(err);
            throw err;
        });
    }

    async sendMediaMessage(file: File, type: string, senderIdentifier: string) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        formData.append("visitor_token", this.visitorToken || '');
        formData.append("sender_identifier", senderIdentifier);
        return this.client.post('/message/upload', formData).catch(err => {
            console.log(err);
            throw err;
        });
    }

    /**
     * IMBEE-9629 — submit the post-chat rating.
     *
     * Session id and visitor token are passed in rather than read from `this`: by the time the
     * visitor submits, the end-chat flow has already cleared the session id and minted a new
     * visitor token, so the instance no longer holds the pair this rating belongs to.
     */
    async submitSurvey(sessionId: string, visitorToken: string, rating: number) {
        return this.client.post(`/sessions/${sessionId}/survey`, {
            visitor_token: visitorToken,
            rating,
        });
    }

    /**
     * IMBEE-10010 — returns `false` when the call failed, rather than an indistinguishable
     * `undefined`. The caller needs to know: the survey endpoint rejects a rating unless the
     * session is already `Ended`, so a swallowed failure here is what makes a rating unsavable
     * several seconds later, with nothing pointing back to the cause.
     */
    async endSession() {
        return this.client.post(`/sessions/${this.sessionId}/end`, {
            visitor_token: this.visitorToken,
        }).catch((err) => {
            console.error('[webchat] endSession failed', err);
            return false as const;
        });
    }

    async fetchMessages(offsetTimestamp?: number) {
        return this.client.get('/message', {
            params: {
                ...(offsetTimestamp ? {
                    offset_timestamp: offsetTimestamp / 1000
                } : {}),
            },
        });
    }

    /**
     * IMBEE-8954 (AC-4.3(b)) — `campaignShowId` is an ADDITIVE, OPTIONAL request field. When the
     * session that follows a campaign greeting carries the show's id, the service stamps
     * `session.campaign` inside the same write that creates the session and back-fills
     * `conversationStartedAt` onto the show record. Without it `conversationStartedAt` is never
     * set, so every real reply lands in `repliedNoConversation` and the `engaged`,
     * `conversations` and `engagementRate` tiles read zero while campaigns work correctly.
     *
     * ⚠ camelCase, deliberately, in a body whose other fields are snake_case. The service runs
     * `ValidationPipe({whitelist: true})`, which STRIPS an undeclared property without erroring —
     * so `campaign_show_id` returns a 201 with the stamp silently missing and no error anywhere.
     * The failure would present as "campaign attribution is flaky", months later.
     *
     * Omitted when absent rather than sent as null: a session created without it behaves exactly
     * as today, and an invalid or foreign id is ignored by the service, never rejected — losing
     * attribution is a reporting gap, losing the conversation would be a product failure, and the
     * two must not share a failure mode.
     */
    async createSession(formValues: Record<string, any>, campaignShowId?: string) {
        return this.client.post('/sessions', {
            form: formValues,
            visitor_token: this.visitorToken,
            ...(campaignShowId ? { campaignShowId } : {}),
        }).then(response => {
            const data = response.data;
            this.setSessionId(data.session_id || '');
            this.setAccessToken(data.access_token || '');
            this.setRefreshToken(data.refresh_token || '');
            return response;
        });
    }

    async putMessageRating({id, type, comment}: {id: string, type: MessageRatingType, comment?: string}) {
        return this.client.put(`/message/${id}/ratings`, {
            type,
            ...(comment ? {
                comment,
            }: {}),
        });
    }
}

const client = new ApiClient();

export default client;